import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { ConfigProvider, DatePicker, theme as antTheme } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useTheme } from "../context/ThemeContext";

const { RangePicker } = DatePicker;

const CHIPS = ["D", "W", "1M", "3M", "YTD", "1Y"];

function chipToRange(chip, today = new Date()) {
  const end = new Date(today);
  const start = new Date(today);

  switch (chip) {
    case "D":
      start.setDate(end.getDate() - 1);
      break;
    case "W":
      start.setDate(end.getDate() - 7);
      break;
    case "1M":
      start.setMonth(end.getMonth() - 1);
      break;
    case "3M":
      start.setMonth(end.getMonth() - 3);
      break;
    case "YTD":
      start.setMonth(0, 1);
      break;
    case "1Y":
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      break;
  }

  const fmt = (d) => d.toISOString().slice(0, 10);

  return {
    start: fmt(start),
    end: fmt(end),
  };
}

const defaultRange = chipToRange("1M");

export const DEFAULT_FILTERS = {
  account: [],
  meter: [],
  category: [],
  mode: "Interval",
  chip: "1M",
  start: defaultRange.start,
  end: defaultRange.end,
  interval: "Day",
};

function MultiSelect({
  label,
  placeholder,
  options = [],
  valueKey,
  labelKey,
  selected = [],
  onChange,
  colors,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Keep original full dropdown list
  const [allOptions, setAllOptions] = useState(options);

  useEffect(() => {
    if (options.length > allOptions.length) {
      setAllOptions(options);
    }

    if (allOptions.length === 0 && options.length > 0) {
      setAllOptions(options);
    }
  }, [options, allOptions.length]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }

    // Important: keep dropdown open for multi-select
    setOpen(true);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setOpen(false);
  };

  const dropdownOptions = allOptions.length > 0 ? allOptions : options;

  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length > 1
        ? `${selected.length} selected`
        : (() => {
            const opt = dropdownOptions.find(
              (o) => o[valueKey] === selected[0],
            );

            return opt ? opt[labelKey] : selected[0];
          })();

  return (
    <div className="relative" ref={ref}>
      <div
        className="mb-2 text-xs uppercase tracking-wide"
        style={{ color: colors.textSecondary }}
      >
        {label}
      </div>

      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex min-h-[44px] w-full items-center justify-between rounded-md border px-3 py-2 text-left"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
        }}
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          {selected.length === 0 ? (
            <span
              className="block truncate text-sm"
              style={{ color: colors.text }}
            >
              {displayText}
            </span>
          ) : (
            <span
              className="inline-block max-w-full truncate rounded-full px-3 py-1 text-xs"
              style={{
                backgroundColor: `${colors.primary}20`,
                color: colors.primary,
              }}
            >
              {displayText}
            </span>
          )}
        </div>

        <span className="ml-2 text-xs" style={{ color: colors.textSecondary }}>
          ▼
        </span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-[9999] mt-1 max-h-[260px] w-full overflow-y-auto rounded-md border shadow-xl"
          style={{
            backgroundColor: colors.background,
            borderColor: colors.border,
          }}
        >
          <button
            type="button"
            onClick={clearAll}
            className="block w-full border-b px-4 py-2 text-left text-sm transition-all"
            style={{
              borderColor: colors.border,
              color: colors.primary,
            }}
          >
            × Clear all
          </button>

          {dropdownOptions.map((opt) => {
            const id = opt[valueKey];
            const checked = selected.includes(id);

            return (
              <button
                type="button"
                key={id}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(id);
                }}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-all"
                style={{
                  color: colors.text,
                }}
              >
                <span
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border"
                  style={{
                    borderColor: checked
                      ? colors.primary
                      : colors.textSecondary,
                    backgroundColor: checked
                      ? colors.primary
                      : colors.background,
                  }}
                >
                  {checked && (
                    <span className="text-[11px] leading-none text-white">
                      ✓
                    </span>
                  )}
                </span>

                <span className="truncate text-sm font-medium">
                  {opt[labelKey]}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default function WaterUsageFilters({
  accounts = [],
  meters = [],
  categories = [],
  value = DEFAULT_FILTERS,
  onChange = () => {},
  onDownload = () => {},
}) {

  const { colors, theme } = useTheme();

const appliedFilters = useMemo(
  () => ({
    ...DEFAULT_FILTERS,
    ...value,
  }),
  [value]
);

const [draftFilters, setDraftFilters] = useState(appliedFilters);

useEffect(() => {
  setDraftFilters(appliedFilters);
}, [appliedFilters]);

const f = draftFilters;

  // useEffect(() => {
  //   if (f.chip && (!f.start || !f.end)) {
  //     const { start, end } = chipToRange(f.chip);

  //     onChange({
  //       ...f,
  //       start,
  //       end,
  //     });
  //   }

  // }, []);

  // const set = (patch) => {
  //   onChange({
  //     ...f,
  //     ...patch,
  //   });
  // };
  // Inside WaterUsageFilters.jsx

const set = (patch) => {
  setDraftFilters((prev) => ({
    ...prev,
    ...patch,
  }));
};

const onDateInput = (key, val) => {
  if (!val) return;

  set({
    [key]: val,
    mode: "Custom",
    chip: null,
  });
};

  const onChipClick = (chip) => {
    const { start, end } = chipToRange(chip);

    set({
      chip,
      mode: "Interval",
      start,
      end,
    });
  };

  const onModeClick = (mode) => {
    if (mode === "Custom") {
      set({
        mode: "Custom",
        chip: null,
      });
      return;
    }

    set({
      mode,
      chip: f.chip || "1M",
    });
  };

  const handleApply = () => {
  onChange({
    ...draftFilters,
  });
};

const handleCancel = () => {
  const resetFilters = {
    ...DEFAULT_FILTERS,
  };

  setDraftFilters(resetFilters);
  onChange(resetFilters);
};

  // const onDateInput = (key, val) => {
  //   set({
  //     [key]: val,
  //     mode: "Custom",
  //     chip: null,
  //   });
  // };
  // const onDateInput = (key, val) => {
  //   // We update the local object and immediately push the whole state up
  //   const newFilters = {
  //     ...f,
  //     [key]: val,
  //     mode: "Custom",
  //     chip: null,
  //   };
  //   onChange(newFilters);
  // };

  return (
    <div
      className="mt-5 overflow-visible rounded-xl border"
      style={{
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
      }}
    >
      {/* TOP FILTERS */}
      <div
        className="grid grid-cols-1 gap-4 border-b p-4 md:grid-cols-3"
        style={{ borderColor: colors.border }}
      >
        <MultiSelect
          label="Accounts"
          placeholder={`All accounts${accounts.length ? ` (${accounts.length})` : ""}`}
          options={accounts}
          valueKey="id"
          labelKey="name"
          selected={f.account || []}
          onChange={(account) => set({ account })}
          colors={colors}
        />

        <MultiSelect
          label="Meters"
          placeholder={`All meters${meters.length ? ` (${meters.length})` : ""}`}
          options={meters}
          valueKey="id"
          labelKey="name"
          selected={f.meter || []}
          onChange={(meter) => set({ meter })}
          colors={colors}
        />

        <MultiSelect
          label="Category"
          placeholder="All"
          options={categories}
          valueKey="code"
          labelKey="name"
          selected={f.category || []}
          onChange={(category) => set({ category })}
          colors={colors}
        />
      </div>

      {/* BOTTOM FILTERS */}
      <div className="flex flex-wrap items-end justify-between gap-5 p-4">
        <div>
          <div className="mb-3 flex items-center gap-2 text-sm">
            {/* {["Range", "Interval", "Custom"].map((mode, index) => ( */}
            {["Range"].map((mode, index) => (
              <React.Fragment key={mode}>
                <button
                  type="button"
                  onClick={() => onModeClick(mode)}
                  className="transition-all text-[12px]"
                  style={{
                    color:
                      f.mode === mode ? colors.primary : colors.textSecondary,
                    fontWeight: f.mode === mode ? 600 : 400,
                  }}
                >
                  {mode}
                </button>

                {/* {index < 2 && <span className="text-[#7ea6d9]">|</span>} */}
              </React.Fragment>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {CHIPS.map((chip) => (
              <button
                type="button"
                key={chip}
                onClick={() => onChipClick(chip)}
                className="rounded-md px-4 py-2 text-sm transition-all text-[12px]"
                style={{
                  backgroundColor:
                    f.chip === chip ? colors.primary : colors.background,
                  color:
                    f.chip === chip
                      ? colors.buttonText || "#ffffff"
                      : colors.textSecondary,
                  border:
                    f.chip === chip
                      ? `1px solid ${colors.primary}`
                      : `1px solid ${colors.border}`,
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {/* CUSTOM SECTION */}
          {/* <div>
      <div className="mb-2 text-xs font-semibold text-[12px] tracking-wide text-[#7ea6d9]">
        Custom
      </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={f.start || ""}
              onChange={(e) => onDateInput("start", e.target.value)}
              className="h-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] px-4 text-sm text-[#d9e8ff] outline-none"
            />

            <span className="text-[#7ea6d9]">⇋</span>

            <input
              type="date"
              value={f.end || ""}
              onChange={(e) => onDateInput("end", e.target.value)}
              className="h-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] px-4 text-sm text-[#d9e8ff] outline-none"
            />
            <button
              type="button"
              className="h-[40px] w-[40px] rounded-md border border-[#2a4a73] bg-[#1a3354] text-[#8ba1c7] text-[12px]"
            >
              🗓
            </button>
          </div>
          </div> */}
          {/* CUSTOM SECTION */}
          <div>
            <div
              className="mb-2 text-xs font-semibold text-[12px] tracking-wide"
              style={{ color: colors.textSecondary }}
            >
              Custom
            </div>

            <div
              className="h-[40px] flex items-center rounded-md border px-2"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              <ConfigProvider
                theme={{
                  algorithm:
                    theme === "white"
                      ? antTheme.defaultAlgorithm
                      : antTheme.darkAlgorithm,
                  token: {
                    colorPrimary: colors.primary,
                    colorBgContainer: colors.background,
                    colorText: colors.text,
                    colorTextPlaceholder: colors.textSecondary,
                    colorBorder: "transparent",
                    controlHeight: 36,
                    borderRadius: 8,
                  },
                }}
              >
                <RangePicker
                  value={[
                    f.start ? dayjs(f.start, "YYYY-MM-DD") : null,
                    f.end ? dayjs(f.end, "YYYY-MM-DD") : null,
                  ]}
                  onChange={(dates, dateStrings) => {
                    const [start, end] = dateStrings;

                    if (start && end) {
                      set({
                        start,
                        end,
                        mode: "Custom",
                        chip: null,
                      });
                    }
                  }}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  placeholder={["Start", "End"]}
                  variant="borderless"
                  separator={
                    <SwapOutlined style={{ color: colors.textSecondary }} />
                  }
                  className="custom-range-picker cursor-pointer"
                  style={{
                    width: "240px",
                    background: "transparent",
                    color: colors.text,
                  }}
                  popupClassName="mizu-calendar-popup"
                />
              </ConfigProvider>
            </div>
          </div>

          <div>
            <div
              className="mb-2 text-xs font-semibold text-[12px] tracking-wide"
              style={{ color: colors.textSecondary }}
            >
              Interval
            </div>

            <div
              className="flex overflow-hidden rounded-md border text-[12px]"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              {["Hour", "Day", "Week"].map((interval) => (
                <button
                  type="button"
                  key={interval}
                  onClick={() => set({ interval })}
                  className="px-4 py-2 text-sm transition-all"
                  style={{
                    backgroundColor:
                      f.interval === interval ? colors.primary : "transparent",
                    color:
                      f.interval === interval
                        ? colors.buttonText || "#ffffff"
                        : colors.textSecondary,
                  }}
                >
                  {interval}
                </button>
              ))}
            </div>
          </div>
<div className="flex items-end gap-2">
  <button
    type="button"
    onClick={handleCancel}
    className="h-[40px] rounded-md border px-5 text-sm font-semibold transition-all cursor-pointer"
    style={{
      backgroundColor: colors.background,
      borderColor: colors.border,
      color: colors.textSecondary,
    }}
  >
    Clear
  </button>

  <button
    type="button"
    onClick={handleApply}
    className="h-[40px] rounded-md border px-5 text-sm font-semibold transition-all cursor-pointer"
    style={{
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.buttonText || "#ffffff",
    }}
  >
    Apply
  </button>
</div>
          <button
            type="button"
            onClick={onDownload}
            className="h-[40px] w-[40px] rounded-md border transition-all cursor-pointer"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
              color: colors.textSecondary,
            }}
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}
